<template>
  <div class="pa-4">
    <breadcrumb />

    <div class="flex-wrap mb-4 d-flex align-center justify-space-between">
      <div class="d-flex align-center">
        <h2 class="mr-3 black--text">{{ $t("reports.anomalyDetection.title") }}</h2>
        <v-btn small rounded color="primary" depressed @click="showGuide = true">
          <v-icon left small>mdi-help-circle-outline</v-icon>
          {{ $t("reports.anomalyDetection.aiReportGuide") }}
        </v-btn>
      </div>
      <v-btn color="primary" depressed @click="goBack">{{ $t("reports.anomalyDetection.back") }}</v-btn>
    </div>

    <v-card class="pa-4" outlined v-if="baseReport">
      <!-- Meta -->
      <h3 class="mb-3 black--text">{{ baseReport.farmName ? baseReport.farmName + " Report" : $t("reports.anomalyDetection.title") }}</h3>
      <v-row class="mb-1">
        <v-col cols="6" md="3">
          <div class="grey--text text-caption">{{ $t("reports.anomalyDetection.dateOfReport") }}</div>
          <div class="font-weight-bold">{{ baseReport.createdAt | formatDate }}</div>
        </v-col>
        <v-col cols="6" md="3">
          <div class="grey--text text-caption">{{ $t("reports.anomalyDetection.farm") }}</div>
          <div class="font-weight-bold">{{ baseReport.farmName || "-" }}</div>
        </v-col>
        <v-col cols="6" md="3">
          <div class="grey--text text-caption">{{ $t("reports.anomalyDetection.zone") }}</div>
          <div class="font-weight-bold">{{ baseReport.zoneName || "-" }}</div>
        </v-col>
        <v-col cols="6" md="3">
          <div class="grey--text text-caption">{{ $t("reports.anomalyDetection.location") }}</div>
          <div class="font-weight-bold">{{ baseReport.locationName || "-" }}</div>
        </v-col>
      </v-row>
      <v-row class="mb-2">
        <v-col cols="6" md="3">
          <div class="grey--text text-caption">{{ $t("reports.anomalyDetection.startDate") }}</div>
          <div class="font-weight-bold">{{ baseReport.selectedDate | formatDate }}</div>
        </v-col>
        <v-col cols="6" md="3">
          <div class="grey--text text-caption">{{ $t("reports.anomalyDetection.numberOfHistoricalReports") }}</div>
          <div class="font-weight-bold">{{ baseReport.historicalReports }}</div>
        </v-col>
      </v-row>

      <v-divider class="my-3" />

      <h3 class="mb-2 black--text">{{ $t("reports.anomalyDetection.imageComparison") }}</h3>
      <v-tabs v-model="tab" class="mb-4">
        <v-tab>{{ $t("reports.anomalyDetection.sideComparison") }}</v-tab>
        <v-tab>{{ $t("reports.anomalyDetection.sliderComparison") }}</v-tab>
        <v-tab>{{ $t("reports.anomalyDetection.transparencyComparison") }}</v-tab>
      </v-tabs>

      <!-- Date selectors -->
      <v-row>
        <v-col cols="12" md="6">
          <label class="grey--text text-caption">{{ $t("reports.anomalyDetection.selectDate") }}</label>
          <v-select v-model="dateA" :items="dateItems" outlined dense hide-details :loading="loadingA" />
        </v-col>
        <v-col cols="12" md="6">
          <label class="grey--text text-caption">{{ $t("reports.anomalyDetection.selectDate") }}</label>
          <v-select v-model="dateB" :items="dateItems" outlined dense hide-details :loading="loadingB" />
        </v-col>
      </v-row>

      <!-- Side by side -->
      <div v-if="tab === 0" class="mt-4">
        <v-row>
          <v-col cols="12" md="6">
            <div class="map-holder">
              <div v-if="loadingAActive" class="map-loading">
                <v-progress-circular indeterminate color="primary" size="36" />
                <div v-if="aGenerating" class="mt-2 grey--text">{{ $t("reports.anomalyDetection.generating") }}</div>
              </div>
              <index-overlay-map :overlays="overlaysA" :outline-coordinates="outline" :circle="circleOutline" height="460px" />
            </div>
          </v-col>
          <v-col cols="12" md="6">
            <div class="map-holder">
              <div v-if="loadingBActive" class="map-loading">
                <v-progress-circular indeterminate color="primary" size="36" />
                <div v-if="bGenerating" class="mt-2 grey--text">{{ $t("reports.anomalyDetection.generating") }}</div>
              </div>
              <index-overlay-map :overlays="overlaysB" :outline-coordinates="outline" :circle="circleOutline" height="460px" />
            </div>
          </v-col>
        </v-row>
      </div>

      <!-- Slider -->
      <div v-else-if="tab === 1" class="mt-4 slider-wrap map-holder" ref="sliderWrap">
        <div v-if="comboBusy" class="map-loading">
          <v-progress-circular indeterminate color="primary" size="36" />
          <div v-if="comboGenerating" class="mt-2 grey--text">{{ $t("reports.anomalyDetection.generating") }}</div>
        </div>
        <index-overlay-map
          :overlays="sliderOverlays"
          :outline-coordinates="outline"
          :circle="circleOutline"
          height="520px"
          @overlay-rects="onOverlayRects"
        />
        <div v-if="overlayRect && !comboBusy" class="slider-divider" :style="dividerStyle">
          <div class="slider-handle" @mousedown="startDrag"><v-icon small color="black">mdi-arrow-left-right</v-icon></div>
        </div>
      </div>

      <!-- Transparency -->
      <div v-else class="mt-4">
        <div class="map-holder">
          <div v-if="comboBusy" class="map-loading">
            <v-progress-circular indeterminate color="primary" size="36" />
            <div v-if="comboGenerating" class="mt-2 grey--text">{{ $t("reports.anomalyDetection.generating") }}</div>
          </div>
          <index-overlay-map :overlays="transparencyOverlays" :outline-coordinates="outline" :circle="circleOutline" height="520px" />
        </div>
        <transparency-slider
          v-model="opacity"
          :label="$t('reports.anomalyDetection.transparencySlider')"
          class="mt-3"
        />
      </div>

      <v-divider class="my-4" />

      <!-- Report details: both dates' bands side by side -->
      <h4 class="mb-3 primary--text">{{ $t("reports.anomalyDetection.reportDetails") }}</h4>
      <v-row>
        <v-col cols="12" md="6">
          <anomaly-bands :bands="bandsOf(reportA)" />
        </v-col>
        <v-col cols="12" md="6">
          <anomaly-bands :bands="bandsOf(reportB)" />
        </v-col>
      </v-row>

      <anomaly-colorbar class="mt-4" />

      <v-alert type="warning" text class="mt-4" border="left" icon="mdi-alert">
        <strong>{{ $t("reports.anomalyDetection.disclaimerTitle") }}</strong><br />
        {{ $t("reports.anomalyDetection.disclaimer") }}
      </v-alert>
    </v-card>

    <guide-dialog :value="showGuide" @input="showGuide = $event" />

    <confirm-box
      v-if="generatePrompt"
      :dialog="generatePrompt"
      :title="$t('reports.anomalyDetection.selectDate')"
      :message="$t('reports.anomalyDetection.notGeneratedPrompt')"
      :confirm-btn-text="$t('reports.anomalyDetection.generateNow')"
      :cancel-btn-text="$t('reports.anomalyDetection.cancel')"
      @continue:action="confirmGenerate"
      @cancel:action="cancelGenerate"
    />
  </div>
</template>

<script>
import AnomalyDetectionService from "@/_services/AnomalyDetectionService";
import IndexOverlayMap from "../crop-progression/components/IndexOverlayMap.vue";
import AnomalyBands from "./components/AnomalyBands.vue";
import AnomalyColorbar from "./components/AnomalyColorbar.vue";
import GuideDialog from "./components/GuideDialog.vue";
import TransparencySlider from "../shared/TransparencySlider.vue";
import ConfirmBox from "@/components/ConfirmBox.vue";
import loadingMixin from "@/mixins/LoadingMixin";
import downloadMixin from "@/mixins/DownloadMixin";
import reportError from "@/mixins/reportError";

const POLL_MS = 4000;
const EMPTY_BANDS = { green: null, yellow: null, red: null };

export default {
  name: "AnomalyDetectionComparison",
  components: { IndexOverlayMap, AnomalyBands, AnomalyColorbar, GuideDialog, TransparencySlider, ConfirmBox },
  mixins: [loadingMixin, downloadMixin, reportError],
  data() {
    return {
      baseReport: null,
      dates: [],
      generatedMap: {},
      tab: 0,
      dateA: null,
      dateB: null,
      prevDateA: null,
      prevDateB: null,
      reportA: null,
      reportB: null,
      loadingA: false,
      loadingB: false,
      opacity: 50,
      sliderPct: 50,
      overlayRect: null,
      showGuide: false,
      generatePrompt: false,
      pendingSlot: null,
      pendingDate: null,
      dragging: false,
      pollTimers: {},
    };
  },
  computed: {
    reportUid() {
      return this.$route.params.reportUid;
    },
    dateItems() {
      return (this.dates || []).map((d) => ({ text: this.$options.filters.formatDate(d), value: d }));
    },
    outline() {
      return (this.baseReport && this.baseReport.coordinates) || [];
    },
    circleOutline() {
      if (this.baseReport && this.baseReport.radius) {
        return { lat: this.baseReport.centerLatitude, lng: this.baseReport.centerLongitude, radiusKm: this.baseReport.radius };
      }
      return null;
    },
    overlaysA() {
      return this.singleOverlay(this.reportA);
    },
    overlaysB() {
      return this.singleOverlay(this.reportB);
    },
    sliderOverlays() {
      const a = this.imgOf(this.reportA);
      const b = this.imgOf(this.reportB);
      const out = [];
      // B is the base layer (revealed on the right); A sits on top clipped to the
      // left of the divider, so report A always reads on the left and B on the right.
      if (b) out.push({ url: b.url, bbox: b.bbox, opacity: 1, clipRightPct: null });
      if (a) out.push({ url: a.url, bbox: a.bbox, opacity: 1, clipRightPct: b ? this.sliderPct : null });
      return out;
    },
    transparencyOverlays() {
      const a = this.imgOf(this.reportA);
      const b = this.imgOf(this.reportB);
      const out = [];
      if (a) out.push({ url: a.url, bbox: a.bbox, opacity: 1, clipRightPct: null });
      if (b) out.push({ url: b.url, bbox: b.bbox, opacity: this.opacity / 100, clipRightPct: null });
      return out;
    },
    aGenerating() {
      return !!(this.reportA && this.reportA.status === "GENERATING");
    },
    bGenerating() {
      return !!(this.reportB && this.reportB.status === "GENERATING");
    },
    loadingAActive() {
      return this.loadingA || this.aGenerating;
    },
    loadingBActive() {
      return this.loadingB || this.bGenerating;
    },
    comboBusy() {
      return this.loadingAActive || this.loadingBActive;
    },
    comboGenerating() {
      return this.aGenerating || this.bGenerating;
    },
    dividerStyle() {
      if (!this.overlayRect) return {};
      const left = this.overlayRect.left + (this.sliderPct / 100) * this.overlayRect.width;
      return { left: left + "px" };
    },
  },
  watch: {
    dateA(val, old) {
      this.prevDateA = old;
      this.onDateChange("A", val);
    },
    dateB(val, old) {
      this.prevDateB = old;
      this.onDateChange("B", val);
    },
  },
  created() {
    this.init();
  },
  beforeDestroy() {
    Object.values(this.pollTimers).forEach((t) => clearInterval(t));
    window.removeEventListener("mousemove", this.onDrag);
    window.removeEventListener("mouseup", this.stopDrag);
  },
  methods: {
    // Each report is standalone (one analyzed date).
    imgOf(report) {
      if (!report || !report.bbox || !report.imageUrl) return null;
      return { url: report.imageUrl, bbox: report.bbox };
    },
    bandsOf(report) {
      return (report && report.bands) || EMPTY_BANDS;
    },
    singleOverlay(report) {
      const img = this.imgOf(report);
      return img ? [{ url: img.url, bbox: img.bbox, opacity: 1, clipRightPct: null }] : [];
    },
    async init() {
      this.startLoading();
      try {
        const env = await AnomalyDetectionService.getReport(this.reportUid);
        this.baseReport = (env && env.data) || null;
        if (this.baseReport) {
          this.reportA = this.baseReport;
          this.dateA = this.baseReport.selectedDate;
          this.prevDateA = this.dateA;
        }
        const rel = await AnomalyDetectionService.getRelatedDates(this.reportUid);
        const d = (rel && rel.data) || {};
        this.dates = d.dates || [];
        this.generatedMap = d.generated || {};
        const otherGenerated = Object.keys(this.generatedMap).find((dt) => dt !== this.dateA);
        if (otherGenerated) {
          this.dateB = otherGenerated;
          this.prevDateB = otherGenerated;
        }
      } finally {
        this.stopLoading();
      }
    },
    onDateChange(slot, date) {
      if (!date) return;
      if (this.baseReport && date === this.baseReport.selectedDate) {
        this.setSlotReport(slot, this.baseReport);
        return;
      }
      const gen = this.generatedMap[date];
      if (gen && gen.reportUid) {
        this.loadSlot(slot, gen.reportUid);
      } else {
        this.pendingSlot = slot;
        this.pendingDate = date;
        this.generatePrompt = true;
      }
    },
    setSlotReport(slot, report) {
      if (slot === "A") this.reportA = report;
      else this.reportB = report;
    },
    async loadSlot(slot, uid) {
      this.setLoading(slot, true);
      try {
        const env = await AnomalyDetectionService.getReport(uid);
        const report = (env && env.data) || null;
        this.setSlotReport(slot, report);
        if (report && report.status === "GENERATING") this.pollSlot(slot, uid);
      } catch (e) {
        this.$notify({ text: this.$t("reports.anomalyDetection.loadFailed"), type: "error" });
      } finally {
        this.setLoading(slot, false);
      }
    },
    setLoading(slot, val) {
      if (slot === "A") this.loadingA = val;
      else this.loadingB = val;
    },
    pollSlot(slot, uid) {
      if (this.pollTimers[slot]) clearInterval(this.pollTimers[slot]);
      this.pollTimers[slot] = setInterval(async () => {
        const env = await AnomalyDetectionService.getReport(uid);
        const report = (env && env.data) || null;
        this.setSlotReport(slot, report);
        if (!report || report.status !== "GENERATING") {
          clearInterval(this.pollTimers[slot]);
          this.pollTimers[slot] = null;
        }
      }, POLL_MS);
    },
    async confirmGenerate() {
      this.generatePrompt = false;
      const slot = this.pendingSlot;
      const date = this.pendingDate;
      if (!date) return;
      this.setLoading(slot, true);
      try {
        const env = await AnomalyDetectionService.compareReports({
          reportUid: this.reportUid,
          dates: [date],
        });
        const report = env && env.data && env.data.reports && env.data.reports[0];
        if (report) {
          this.setSlotReport(slot, report);
          this.generatedMap = { ...this.generatedMap, [date]: { reportUid: report.reportUid, status: report.status } };
          if (report.status === "GENERATING") this.pollSlot(slot, report.reportUid);
        } else {
          this.$notify({
            text: this.reportErrorMessage({ data: env }, "reports.anomalyDetection.generateFailed"),
            type: "error",
          });
        }
      } catch (e) {
        this.$notify({
          text: this.reportErrorMessage(e, "reports.anomalyDetection.generateFailed"),
          type: "error",
        });
        this.revertSlot(slot);
      } finally {
        this.setLoading(slot, false);
        this.pendingSlot = null;
        this.pendingDate = null;
      }
    },
    cancelGenerate() {
      this.generatePrompt = false;
      this.revertSlot(this.pendingSlot);
      this.pendingSlot = null;
      this.pendingDate = null;
    },
    revertSlot(slot) {
      if (slot === "A") this.dateA = this.prevDateA;
      else this.dateB = this.prevDateB;
    },
    onOverlayRects(rects) {
      const valid = (rects || []).filter(Boolean);
      this.overlayRect = valid.length ? valid[valid.length - 1] : null;
    },
    startDrag() {
      this.dragging = true;
      window.addEventListener("mousemove", this.onDrag);
      window.addEventListener("mouseup", this.stopDrag);
    },
    onDrag(e) {
      if (!this.dragging || !this.$refs.sliderWrap || !this.overlayRect) return;
      const wrapRect = this.$refs.sliderWrap.getBoundingClientRect();
      const imageLeft = wrapRect.left + this.overlayRect.left;
      let pct = ((e.clientX - imageLeft) / this.overlayRect.width) * 100;
      pct = Math.max(0, Math.min(100, pct));
      this.sliderPct = Math.round(pct);
    },
    stopDrag() {
      this.dragging = false;
      window.removeEventListener("mousemove", this.onDrag);
      window.removeEventListener("mouseup", this.stopDrag);
    },
    goBack() {
      this.$router.push({ name: "anomalyDetectionView", params: { reportUid: this.reportUid } });
    },
  },
};
</script>

<style scoped>
.slider-wrap {
  position: relative;
}
.map-holder {
  position: relative;
}
.map-loading {
  position: absolute;
  inset: 0;
  z-index: 5;
  background: rgba(255, 255, 255, 0.85);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
}
.slider-divider {
  position: absolute;
  width: 2px;
  background: #fff;
  z-index: 4;
  transform: translateX(-1px);
  pointer-events: none;
  top: 0px;
  height: 100%;
}
.slider-handle {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 34px;
  height: 34px;
  border-radius: 50%;
  background: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: ew-resize;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.3);
  pointer-events: auto;
}
</style>
